import React, { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TwoActionsDialogService } from '../../services/twoActionsDialog/twoActionsDialogService';
import { FadingTooltip, UPLOAD_FILE_STATUS_KEY } from '../../ui';
import { useVecteurDonnees } from './VecteurDonneesHook';

const DEFAULT_VALUE = {
  id: null,
  name: '',
  content: null,
  file: null,
  status: UPLOAD_FILE_STATUS_KEY.EMPTY,
  isReverted: false,
};

const VecteurDonnees = ({ context }) => {
  const { t } = useTranslation();
  const {
    addTechVar,
    assetClass,
    scenarioId,
    equipmentsList,
    downloadFileFromStorage,
    getAvailableColumnsList,
    dataVectorParameter,
    currentScenarioParametersValues,
  } = useVecteurDonnees();
  const { setValue, getValues } = useFormContext();

  const [missingColumnsList, setMissingColumnsList] = useState([]);
  const [fileHandlingError, setFileHandlingError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const downloadFile = async (assetClass) => {
    setIsLoading(true);
    try {
      await downloadFileFromStorage(assetClass);
    } catch (error) {
      console.error(error);
      setFileHandlingError(t('dataVector.errors.downloadError', 'An error occurred while downloading the file'));
    } finally {
      setIsLoading(false);
    }
  };
  const sortedEquipmentsList = useMemo(() => {
    return [...(equipmentsList ?? [])].sort((a, b) => a.localeCompare(b));
  }, [equipmentsList]);

  const isAssetClassUpdated = (assetClassName) => {
    try {
      const dataVectorValue = currentScenarioParametersValues?.find(
        (param) => param.parameterId === dataVectorParameter?.id
      )?.value;
      const hasPersistedData =
        dataVectorValue && JSON.parse(dataVectorValue).some((item) => item.parameterId === assetClassName);

      return hasPersistedData;
    } catch (error) {
      console.error('Error checking if asset class is updated:', error);
      return false;
    }
  };
  const revertToOriginalDataset = (assetClass) => {
    setMissingColumnsList([]);
    setFileHandlingError(null);
    try {
      const dataVectorValue = getValues('DataVector');
      const parsedDataVector = dataVectorValue ? JSON.parse(dataVectorValue) : [];
      const filteredDataVector = parsedDataVector.filter((item) => item.parameterId !== assetClass);
      setValue('DataVector', JSON.stringify(filteredDataVector), { shouldDirty: true });

      setValue(assetClass, { ...DEFAULT_VALUE, isReverted: true }, { shouldDirty: true });
    } catch (error) {
      console.error(error);
      setFileHandlingError(t('dataVector.errors.restoreError', 'An error occurred during restoration'));
    }
  };

  const onRevertClick = async () => {
    const dialogProps = {
      id: 'revert-data-vector',
      labels: {
        title: t('dataVector.dialogs.revert.title', 'Restore the table?'),
        body: t('dataVector.dialogs.revert.body', 'This will replace your data with the initial dataset data.'),
        button1: t('dataVector.dialogs.revert.cancel', 'Cancel'),
        button2: t('dataVector.dialogs.revert.confirm', 'Restore'),
      },
      button2Props: {
        color: 'error',
      },
    };
    const result = await TwoActionsDialogService.openDialog(dialogProps);
    if (result === 2) {
      revertToOriginalDataset(assetClass);
    }
  };

  const checkRequiredColumnsInHeader = (fileColumns, datasetMetadata) => {
    const missingColumns = [];
    datasetMetadata.forEach((column) => {
      if (!fileColumns.includes(column)) missingColumns.push(column);
    });
    return missingColumns;
  };
  if (equipmentsList?.length === 0)
    return <span>{t('commoncomponents.agingLaws.messages.noData', 'No data to display')}</span>;
  return (
    <div>
      <Grid container spacing={2} style={{ marginBottom: '20px', paddingTop: '10px' }}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="asset-class-selector"
            options={sortedEquipmentsList}
            value={sortedEquipmentsList?.find((option) => option === (assetClass ?? '')) || null}
            onChange={(event, newValue) => {
              event.preventDefault();
              event.stopPropagation();
              setMissingColumnsList([]);
              setFileHandlingError(null);
              addTechVar({ assetClass: newValue }, scenarioId);
            }}
            renderOption={(props, option) => {
              const isUpdated = isAssetClassUpdated(option);
              return (
                <li {...props}>
                  {option}
                  {isUpdated && (
                    <FadingTooltip title={t('dataVector.fileUpdatedTooltip', 'File updated')} placement="right">
                      <span style={{ marginLeft: '8px', color: '#ff9800', fontWeight: 'bold' }}>*</span>
                    </FadingTooltip>
                  )}
                </li>
              );
            }}
            renderInput={(params) => {
              const isUpdated = assetClass && isAssetClassUpdated(assetClass);
              return (
                <TextField
                  {...params}
                  label={
                    <span>
                      {t('dataVector.assetClass', 'Asset Class')}
                      {isUpdated && (
                        <FadingTooltip title={t('dataVector.fileUpdatedTooltip', 'File updated')} placement="top">
                          <span style={{ marginLeft: '4px', color: '#ff9800', fontWeight: 'bold' }}>*</span>
                        </FadingTooltip>
                      )}
                    </span>
                  }
                  fullWidth
                />
              );
            }}
          />
        </Grid>
        <Grid item container xs={12} sm={6} spacing={1} sx={{ direction: 'column', mt: 0 }}>
          {assetClass &&
            (!isLoading ? (
              <Controller
                name={assetClass}
                key={assetClass}
                defaultValue={DEFAULT_VALUE}
                render={({ field }) => {
                  const { value, onChange } = field;
                  return (
                    <Grid sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <Button
                        data-cy="download-asset-class-data-button"
                        variant="contained"
                        color="primary"
                        disabled={
                          !assetClass ||
                          value?.status === UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD ||
                          (value?.isReverted && isAssetClassUpdated(assetClass))
                        }
                        onClick={() => {
                          downloadFile(assetClass);
                        }}
                      >
                        {t('dataVector.downloadCSV', 'Download CSV')}
                      </Button>
                      <Button
                        data-cy="upload-asset-class-data-button"
                        variant="contained"
                        color="secondary"
                        component="label"
                        disabled={!assetClass || !context.editMode}
                        onChange={async (event) => {
                          setMissingColumnsList([]);
                          setFileHandlingError(null);
                          setIsLoading(true);
                          const file = event.target.files[0];
                          event.target.value = null;
                          if (file.type !== 'text/csv') {
                            setFileHandlingError(
                              t('dataVector.errors.invalidFileFormat', 'The file must be in .csv format')
                            );
                            setIsLoading(false);
                            return;
                          }
                          const firstChunk = file.slice(0, 5120);
                          const availableColumns = await getAvailableColumnsList(assetClass);
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const splitChunk = event.target.result.trim().split(/\s*[\r\n]+\s*/g);
                            const fileColumns = splitChunk[0].split(',');
                            const missingColumns = checkRequiredColumnsInHeader(fileColumns, availableColumns);
                            const fileToUpload = {
                              name: file.name,
                              status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                              file,
                            };
                            const assetClassColumnsValuesAsString = getValues('asset_class_columns') ?? '';
                            const parsedAssetClassColumnsValues = assetClassColumnsValuesAsString
                              ? JSON.parse(assetClassColumnsValuesAsString)
                              : {};
                            parsedAssetClassColumnsValues[assetClass] = splitChunk[0];
                            if (missingColumns.length === 0) {
                              onChange(fileToUpload);
                              setValue('asset_class_columns', JSON.stringify(parsedAssetClassColumnsValues));
                            } else {
                              setMissingColumnsList(missingColumns);
                            }
                            setIsLoading(false);
                          };
                          reader.readAsText(firstChunk);
                        }}
                      >
                        {t('dataVector.uploadNewCSV', 'Upload New CSV')}
                        <input accept=".csv" style={{ display: 'none' }} id="upload-csv-file" type="file" />
                      </Button>
                      {isAssetClassUpdated(assetClass) && (
                        <Tooltip title={t('dataVector.revertTooltip', 'Revert')}>
                          <span>
                            <IconButton
                              data-cy="revert-asset-class-data-button"
                              color="primary"
                              disabled={!context.editMode}
                              onClick={onRevertClick}
                            >
                              <ReplayIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Grid>
                  );
                }}
              />
            ) : (
              <CircularProgress />
            ))}
          {missingColumnsList.length > 0 && (
            <Grid item sx={{ alignSelf: 'right' }}>
              <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                <ErrorIcon color="error" />
                <Typography data-cy="missing-columns-message" variant="subtitle" color="error">
                  {t(
                    'dataVector.errors.invalidFileColumns',
                    'The file is not valid. The following columns are required:'
                  )}
                </Typography>
              </Stack>

              <List sx={{ pt: 0, pl: 4 }}>
                {missingColumnsList.map((column) => (
                  <ListItem
                    data-cy={column}
                    sx={{ color: (theme) => theme.palette.error.main }}
                    dense={true}
                    disablePadding={true}
                    key={column}
                  >
                    {column}
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
          {fileHandlingError && (
            <Stack sx={{ flexDirection: 'row', gap: 1 }}>
              <ErrorIcon color="error" />
              <Typography data-cy="file-handling-error" variant="subtitle" color="error">
                {fileHandlingError}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

VecteurDonnees.propTypes = {
  context: PropTypes.object,
};

export default VecteurDonnees;
